// ---------------------------------------------------------------------------
// TrendsPulseBoard -- Marketing-First Trends Dashboard
// ---------------------------------------------------------------------------
// Replaces single-metric TrendsPanel with a multi-metric "Pulse Board":
//   1. KPI strip: all vanity metrics at a glance (value + % change + sparkline)
//   2. Focus chart: click any KPI card to see that metric grouped by dimension
//   3. Dimension pills: quick switch between Platform, Gender, Generation, etc.
//   4. Compare toggle: overlay previous period as dashed lines
// ---------------------------------------------------------------------------

import React, { useState, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import {
  TrendingUp,
  TrendingDown,
  Heart,
  MessageCircle,
  Eye,
  Share2,
  Users,
  BarChart3,
  Hash,
  FileText,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  GitCompareArrows,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type { DataSourceId } from './types';
import type { VanityMetric, GroupByDimension, TimelineDataPoint } from './TrendsPanel';
import { DataLoader } from './DataLoader';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MetricSummary {
  readonly metric: VanityMetric;
  readonly currentValue: number;
  readonly previousValue: number;
  readonly recentHalfValue: number; // second half of period (for % change vs previousValue)
  readonly sparkline: readonly number[]; // last N period values (simplified)
}

export interface TrendsPulseBoardProps {
  /** Summary data for all metrics (KPI cards) */
  readonly summaries: readonly MetricSummary[];
  /** Detailed timeline data for the focused metric (main chart) */
  readonly focusedData: readonly TimelineDataPoint[];
  readonly dataSource: DataSourceId;
  readonly isOpen: boolean;
  readonly isLoading?: boolean;
  readonly error?: Error | string | null;

  // Controlled state
  readonly focusedMetric: VanityMetric;
  readonly onFocusedMetricChange: (metric: VanityMetric) => void;
  readonly groupBy: GroupByDimension;
  readonly onGroupByChange: (groupBy: GroupByDimension) => void;
  readonly compareEnabled?: boolean;
  readonly onCompareToggle?: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRICS_BY_SOURCE: Record<DataSourceId, VanityMetric[]> = {
  profiles: ['total_profiles', 'new_profiles', 'comments', 'avg_sentiment'],
  content: ['likes', 'views', 'comments', 'engagement_rate', 'shares'],
  comments: ['volume', 'sentiment', 'comment_likes'],
  brands: ['total_detections', 'unique_profiles', 'audience_comments'],
  hashtags: ['posts_count', 'engagement_rate'],
  teams: ['total_mentions', 'profiles_count', 'comment_mentions'],
  channels: ['posts', 'likes', 'comments', 'interactions'],
};

const GROUP_BY_OPTIONS: Record<DataSourceId, GroupByDimension[]> = {
  profiles: ['platform', 'gender', 'generation', 'country'],
  content: ['platform', 'gender', 'generation', 'emotion'],
  comments: ['platform', 'gender', 'generation', 'sentiment_range', 'emotion'],
  brands: ['platform', 'category', 'subcategory', 'item'],
  hashtags: ['platform'],
  teams: ['platform'],
  channels: ['channel'],
};

const METRIC_CONFIG: Record<
  VanityMetric,
  {
    icon: React.ReactNode;
    label: string;
    shortLabel: string;
    format: (val: number) => string;
    accentColor: string; // HSL accent for the card
    accentBg: string; // Subtle background tint
  }
> = {
  likes: {
    icon: <Heart className="h-4 w-4" />,
    label: 'Likes',
    shortLabel: 'Likes',
    format: formatCompact,
    accentColor: '#f43f5e',
    accentBg: 'rgba(244, 63, 94, 0.08)',
  },
  views: {
    icon: <Eye className="h-4 w-4" />,
    label: 'Views',
    shortLabel: 'Views',
    format: formatCompact,
    accentColor: '#f59e0b',
    accentBg: 'rgba(245, 158, 11, 0.08)',
  },
  plays: {
    icon: <Eye className="h-4 w-4" />,
    label: 'Plays',
    shortLabel: 'Plays',
    format: formatCompact,
    accentColor: '#f59e0b',
    accentBg: 'rgba(245, 158, 11, 0.08)',
  },
  amplification: {
    icon: <Share2 className="h-4 w-4" />,
    label: 'Shares',
    shortLabel: 'Shares',
    format: formatCompact,
    accentColor: '#8b5cf6',
    accentBg: 'rgba(139, 92, 246, 0.08)',
  },
  saves: {
    icon: <Share2 className="h-4 w-4" />,
    label: 'Saves',
    shortLabel: 'Saves',
    format: formatCompact,
    accentColor: '#8b5cf6',
    accentBg: 'rgba(139, 92, 246, 0.08)',
  },
  engagement_rate: {
    icon: <TrendingUp className="h-4 w-4" />,
    label: 'Engagement',
    shortLabel: 'Engagement',
    format: v => `${v.toFixed(1)}%`,
    accentColor: '#10b981',
    accentBg: 'rgba(16, 185, 129, 0.08)',
  },
  comments: {
    icon: <MessageCircle className="h-4 w-4" />,
    label: 'Comments',
    shortLabel: 'Comments',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  total_profiles: {
    icon: <Users className="h-4 w-4" />,
    label: 'Total Profiles',
    shortLabel: 'Total Profiles',
    format: formatCompact,
    accentColor: '#8b5cf6',
    accentBg: 'rgba(139, 92, 246, 0.08)',
  },
  new_profiles: {
    icon: <Users className="h-4 w-4" />,
    label: 'New Active Audience',
    shortLabel: 'New Audience',
    format: formatCompact,
    accentColor: '#06b6d4',
    accentBg: 'rgba(6, 182, 212, 0.08)',
  },
  avg_sentiment: {
    icon: <TrendingUp className="h-4 w-4" />,
    label: 'Avg. Sentiment',
    shortLabel: 'Avg. Sentiment',
    format: v => v.toFixed(1),
    accentColor: '#10b981',
    accentBg: 'rgba(16, 185, 129, 0.08)',
  },
  shares: {
    icon: <Share2 className="h-4 w-4" />,
    label: 'Shares',
    shortLabel: 'Shares',
    format: formatCompact,
    accentColor: '#8b5cf6',
    accentBg: 'rgba(139, 92, 246, 0.08)',
  },
  volume: {
    icon: <MessageCircle className="h-4 w-4" />,
    label: 'Comments Volume',
    shortLabel: 'Comments Volume',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  comment_likes: {
    icon: <Heart className="h-4 w-4" />,
    label: 'Comment Likes',
    shortLabel: 'Comment Likes',
    format: formatCompact,
    accentColor: '#f43f5e',
    accentBg: 'rgba(244, 63, 94, 0.08)',
  },
  replies: {
    icon: <MessageCircle className="h-4 w-4" />,
    label: 'Replies',
    shortLabel: 'Replies',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  sentiment: {
    icon: <TrendingUp className="h-4 w-4" />,
    label: 'Sentiment',
    shortLabel: 'Sentiment',
    format: v => v.toFixed(1),
    accentColor: '#10b981',
    accentBg: 'rgba(16, 185, 129, 0.08)',
  },
  posts_count: {
    icon: <BarChart3 className="h-4 w-4" />,
    label: 'Posts',
    shortLabel: 'Posts',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  profiles_count: {
    icon: <Users className="h-4 w-4" />,
    label: 'Profiles',
    shortLabel: 'Profiles',
    format: formatCompact,
    accentColor: '#06b6d4',
    accentBg: 'rgba(6, 182, 212, 0.08)',
  },
  total_mentions: {
    icon: <Hash className="h-4 w-4" />,
    label: 'Total Mentions',
    shortLabel: 'Total Mentions',
    format: formatCompact,
    accentColor: '#f59e0b',
    accentBg: 'rgba(245, 158, 11, 0.08)',
  },
  profile_mentions: {
    icon: <Users className="h-4 w-4" />,
    label: 'Profile Mentions',
    shortLabel: 'Profile Mentions',
    format: formatCompact,
    accentColor: '#06b6d4',
    accentBg: 'rgba(6, 182, 212, 0.08)',
  },
  post_mentions: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Post Mentions',
    shortLabel: 'Post Mentions',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  comment_mentions: {
    icon: <MessageCircle className="h-4 w-4" />,
    label: 'Comment Mentions',
    shortLabel: 'Comment Mentions',
    format: formatCompact,
    accentColor: '#8b5cf6',
    accentBg: 'rgba(139, 92, 246, 0.08)',
  },
  total_detections: {
    icon: <Hash className="h-4 w-4" />,
    label: 'Total Detections',
    shortLabel: 'Total Detections',
    format: formatCompact,
    accentColor: '#f59e0b',
    accentBg: 'rgba(245, 158, 11, 0.08)',
  },
  profile_detections: {
    icon: <Users className="h-4 w-4" />,
    label: 'Profile Detections',
    shortLabel: 'Profile Detections',
    format: formatCompact,
    accentColor: '#06b6d4',
    accentBg: 'rgba(6, 182, 212, 0.08)',
  },
  post_detections: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Post Detections',
    shortLabel: 'Post Detections',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  unique_profiles: {
    icon: <Users className="h-4 w-4" />,
    label: 'Unique Profiles',
    shortLabel: 'Unique Profiles',
    format: formatCompact,
    accentColor: '#06b6d4',
    accentBg: 'rgba(6, 182, 212, 0.08)',
  },
  audience_comments: {
    icon: <MessageCircle className="h-4 w-4" />,
    label: 'Audience Comments',
    shortLabel: 'Audience Comments',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  active_profiles: {
    icon: <Users className="h-4 w-4" />,
    label: 'Active Profiles',
    shortLabel: 'Active Profiles',
    format: formatCompact,
    accentColor: '#10b981',
    accentBg: 'rgba(16, 185, 129, 0.08)',
  },
  audience_likes: {
    icon: <Heart className="h-4 w-4" />,
    label: 'Audience Likes',
    shortLabel: 'Audience Likes',
    format: formatCompact,
    accentColor: '#f43f5e',
    accentBg: 'rgba(244, 63, 94, 0.08)',
  },
  posts: {
    icon: <FileText className="h-4 w-4" />,
    label: 'Posts',
    shortLabel: 'Posts',
    format: formatCompact,
    accentColor: '#3b82f6',
    accentBg: 'rgba(59, 130, 246, 0.08)',
  },
  interactions: {
    icon: <TrendingUp className="h-4 w-4" />,
    label: 'Interactions',
    shortLabel: 'Interactions',
    format: formatCompact,
    accentColor: '#10b981',
    accentBg: 'rgba(16, 185, 129, 0.08)',
  },
};

const GROUP_BY_LABELS: Record<GroupByDimension, string> = {
  platform: 'Platform',
  gender: 'Gender',
  age: 'Age',
  generation: 'Generation',
  country: 'Country',
  emotion: 'Emotion',
  sentiment_range: 'Sentiment',
  category: 'Category',
  subcategory: 'Subcategory',
  item: 'Item',
  channel: 'Channel',
};

const CHART_COLORS = [
  '#E1306C', // vibrant pink
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#f43f5e', // rose
  '#3b82f6', // blue
  '#f97316', // orange
];

const METADATA_KEYS = new Set(['period', 'fullPeriod', 'periodLabel']);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCompact(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

function percentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

function getGroupColor(group: string, groupBy: GroupByDimension, index: number): string {
  if (groupBy === 'platform') {
    const cfg = getPlatformIcon(group);
    if (cfg) return cfg.color;
  }
  if (groupBy === 'gender') {
    const n = group.toLowerCase();
    if (n === 'male' || n === 'm') return '#3b82f6';
    if (n === 'female' || n === 'f') return '#ec4899';
  }
  return CHART_COLORS[index % CHART_COLORS.length];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Tiny inline sparkline rendered with SVG (no Recharts overhead) */
function MiniSparkline({
  data,
  color,
  width = 80,
  height = 28,
}: {
  readonly data: readonly number[];
  readonly color: string;
  readonly width?: number;
  readonly height?: number;
}) {
  if (!data || data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const padding = 2;

  const points = data.map((val, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = padding + (1 - (val - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p}`).join(' ');

  // Area fill path (close at bottom)
  const areaD = `${pathD} L${width - padding},${height - padding} L${padding},${height - padding} Z`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#spark-${color.replace('#', '')})`} />
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Single KPI metric card */
function MetricCard({
  summary,
  isFocused,
  onClick,
}: {
  readonly summary: MetricSummary;
  readonly isFocused: boolean;
  readonly onClick: () => void;
}) {
  const config = METRIC_CONFIG[summary.metric];
  const change = percentChange(summary.recentHalfValue, summary.previousValue);
  const isPositive = change > 0;
  const isNeutral = Math.abs(change) < 0.5;

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex flex-col gap-1 rounded-lg border px-3 py-2.5 text-left transition-all min-w-[120px] flex-1',
        isFocused
          ? 'border-v11-border bg-v11-surface ring-1 ring-v11-border shadow-sm'
          : 'border-transparent bg-transparent hover:bg-v11-surface/50 hover:border-v11-border/50'
      )}
    >
      {/* Focused indicator bar */}
      {isFocused && (
        <div
          className="absolute top-0 left-3 right-3 h-[2px] rounded-full"
          style={{ backgroundColor: config.accentColor }}
        />
      )}

      {/* Metric label + icon */}
      <div className="flex items-center gap-1.5">
        <span
          className="opacity-60"
          style={{ color: isFocused ? config.accentColor : 'hsl(var(--v11-text-muted))' }}
        >
          {config.icon}
        </span>
        <span className="text-[11px] font-medium text-v11-text-muted uppercase tracking-wide">
          {config.shortLabel}
        </span>
      </div>

      {/* Value + Change */}
      <div className="flex items-end justify-between gap-2">
        <span className="text-lg font-bold text-v11-text-primary tabular-nums leading-none">
          {config.format(summary.currentValue)}
        </span>

        {/* % change badge (hidden when no comparison data) */}
        {summary.sparkline.length > 0 && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums leading-none rounded-full px-1.5 py-0.5',
              isNeutral
                ? 'text-v11-text-muted bg-v11-bg-secondary'
                : isPositive
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-red-400 bg-red-500/10'
            )}
          >
            {isNeutral ? (
              <Minus className="h-2.5 w-2.5" />
            ) : isPositive ? (
              <ArrowUpRight className="h-2.5 w-2.5" />
            ) : (
              <ArrowDownRight className="h-2.5 w-2.5" />
            )}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>

      {/* Sparkline */}
      <div className="mt-0.5">
        <MiniSparkline
          data={summary.sparkline}
          color={isFocused ? config.accentColor : `${config.accentColor}50`}
          width={90}
          height={24}
        />
      </div>
    </button>
  );
}

/** Dimension group-by pills */
function DimensionPills({
  options,
  selected,
  onChange,
}: {
  readonly options: readonly GroupByDimension[];
  readonly selected: GroupByDimension;
  readonly onChange: (dim: GroupByDimension) => void;
}) {
  if (options.length <= 1) return null;

  return (
    <div className="flex items-center gap-1 bg-v11-bg-secondary/50 rounded-lg p-0.5">
      {options.map(dim => (
        <button
          key={dim}
          onClick={() => onChange(dim)}
          className={cn(
            'px-2.5 py-1 rounded-md text-xs font-medium transition-all',
            selected === dim
              ? 'bg-v11-surface text-v11-text-primary shadow-sm'
              : 'text-v11-text-muted hover:text-v11-text-secondary'
          )}
        >
          {GROUP_BY_LABELS[dim]}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function TrendsPulseBoard({
  summaries,
  focusedData,
  dataSource,
  isOpen,
  isLoading = false,
  error = null,
  focusedMetric,
  onFocusedMetricChange,
  groupBy,
  onGroupByChange,
  compareEnabled = false,
  onCompareToggle,
}: TrendsPulseBoardProps) {
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const [visibleGroups, setVisibleGroups] = useState<string[]>([]);

  const availableMetrics = METRICS_BY_SOURCE[dataSource] || [];
  const availableGroupBy = GROUP_BY_OPTIONS[dataSource] || [];
  const focusedConfig = METRIC_CONFIG[focusedMetric];

  const MAX_GROUPS = 5;

  // Extract unique groups from focused data, sorted by total descending
  // (largest series renders first = behind, smallest last = on top in area chart)
  const { groups, totalGroupCount } = useMemo(() => {
    if (!focusedData || focusedData.length === 0)
      return { groups: [] as string[], totalGroupCount: 0 };
    const totals = new Map<string, number>();
    focusedData.forEach(point => {
      Object.keys(point).forEach(key => {
        if (!METADATA_KEYS.has(key) && !key.endsWith('_data')) {
          const val = typeof point[key] === 'number' ? (point[key] as number) : 0;
          totals.set(key, (totals.get(key) ?? 0) + val);
        }
      });
    });
    const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key);
    return { groups: sorted.slice(0, MAX_GROUPS), totalGroupCount: sorted.length };
  }, [focusedData]);

  const isGroupsTruncated = totalGroupCount > MAX_GROUPS;

  // Sync visible groups when data changes
  React.useEffect(() => {
    if (groups.length > 0) setVisibleGroups(groups);
  }, [groups]);

  const toggleGroup = useCallback((group: string) => {
    setVisibleGroups(prev => {
      if (prev.includes(group)) {
        return prev.length === 1 ? prev : prev.filter(g => g !== group);
      }
      return [...prev, group];
    });
  }, []);

  // Custom tooltip
  const ChartTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-v11-surface border border-v11-border rounded-lg p-3 shadow-xl backdrop-blur-sm">
        <p className="text-xs font-medium text-v11-text-muted mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry, i) => (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-xs text-v11-text-secondary">{entry.name}</span>
              </div>
              <span className="text-xs font-semibold text-v11-text-primary tabular-nums">
                {focusedConfig.format(entry.value as number)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-v11-border bg-v11-surface p-6 mb-4">
        <DataLoader dataSource="trends" variant="initial" />
      </div>
    );
  }

  const hasError = !!error;
  const hasChartData = focusedData && focusedData.length > 0;

  return (
    <div
      className={cn(
        'rounded-xl border bg-v11-surface mb-4 overflow-hidden',
        hasError ? 'border-red-500/30' : 'border-v11-border'
      )}
    >
      {/* ----------------------------------------------------------------- */}
      {/* KPI Strip                                                         */}
      {/* ----------------------------------------------------------------- */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 overflow-x-auto scrollbar-thin">
          {summaries
            .filter(s => availableMetrics.includes(s.metric))
            .map(summary => (
              <MetricCard
                key={summary.metric}
                summary={summary}
                isFocused={summary.metric === focusedMetric}
                onClick={() =>
                  summary.metric !== 'total_profiles' && onFocusedMetricChange(summary.metric)
                }
              />
            ))}
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Controls Bar                                                      */}
      {/* ----------------------------------------------------------------- */}
      <div className="px-4 py-2 flex items-center justify-between gap-3 border-t border-v11-border/50">
        {/* Left: focused metric label + dimension pills */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span style={{ color: focusedConfig.accentColor }}>{focusedConfig.icon}</span>
            <span className="text-sm font-semibold text-v11-text-primary">
              {focusedConfig.label}
            </span>
            <span className="text-xs text-v11-text-muted">by</span>
            {isGroupsTruncated && (
              <span className="text-[10px] font-medium text-v11-text-muted bg-v11-bg-secondary rounded-full px-1.5 py-0.5">
                Top {MAX_GROUPS}
              </span>
            )}
          </div>
          <DimensionPills
            options={availableGroupBy}
            selected={groupBy}
            onChange={onGroupByChange}
          />
        </div>

        {/* Right: chart type + compare toggle */}
        <div className="flex items-center gap-2">
          {/* Compare toggle */}
          {onCompareToggle && (
            <button
              onClick={onCompareToggle}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border',
                compareEnabled
                  ? 'bg-v11-surface border-v11-border text-v11-text-primary'
                  : 'border-transparent text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-surface/50'
              )}
            >
              <GitCompareArrows className="h-3.5 w-3.5" />
              Compare
            </button>
          )}

          {/* Chart type */}
          <div className="flex items-center gap-0.5 bg-v11-bg-secondary/50 rounded-lg p-0.5">
            <button
              onClick={() => setChartType('line')}
              className={cn(
                'px-2 py-1 rounded-md text-xs font-medium transition',
                chartType === 'line'
                  ? 'bg-v11-surface text-v11-text-primary shadow-sm'
                  : 'text-v11-text-muted hover:text-v11-text-secondary'
              )}
            >
              Line
            </button>
            <button
              onClick={() => setChartType('area')}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition',
                chartType === 'area'
                  ? 'bg-v11-surface text-v11-text-primary shadow-sm'
                  : 'text-v11-text-muted hover:text-v11-text-secondary'
              )}
            >
              <Layers className="h-3 w-3" />
              Area
            </button>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Main Chart                                                        */}
      {/* ----------------------------------------------------------------- */}
      <div className="px-4 pb-2">
        {hasError ? (
          <div className="h-[260px] flex items-center justify-center rounded-lg border border-red-500/20 bg-red-500/5">
            <p className="text-red-400 text-sm">Error loading trends data</p>
          </div>
        ) : !hasChartData ? (
          <div className="h-[260px] flex flex-col items-center justify-center text-center">
            <TrendingUp className="h-10 w-10 text-v11-text-muted mb-2 opacity-40" />
            <p className="text-sm text-v11-text-muted">No trend data available</p>
          </div>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'line' ? (
                <LineChart
                  data={focusedData as TimelineDataPoint[]}
                  margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--v11-border))"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="periodLabel"
                    stroke="hsl(var(--v11-text-muted))"
                    tick={{ fill: 'hsl(var(--v11-text-muted))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--v11-text-muted))"
                    tick={{ fill: 'hsl(var(--v11-text-muted))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={focusedConfig.format}
                    width={52}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  {groups.map((group, i) => {
                    const color = getGroupColor(group, groupBy, i);
                    return (
                      <Line
                        key={group}
                        type="monotone"
                        dataKey={group}
                        name={group}
                        stroke={color}
                        strokeWidth={2}
                        hide={!visibleGroups.includes(group)}
                        dot={{ fill: color, r: 2.5, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--v11-surface))' }}
                      />
                    );
                  })}
                </LineChart>
              ) : (
                <AreaChart
                  data={focusedData as TimelineDataPoint[]}
                  margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
                >
                  <defs>
                    {groups.map((group, i) => {
                      const color = getGroupColor(group, groupBy, i);
                      return (
                        <linearGradient
                          key={`g-${group}`}
                          id={`pulse-grad-${group}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                          <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--v11-border))"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="periodLabel"
                    stroke="hsl(var(--v11-text-muted))"
                    tick={{ fill: 'hsl(var(--v11-text-muted))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="hsl(var(--v11-text-muted))"
                    tick={{ fill: 'hsl(var(--v11-text-muted))', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={focusedConfig.format}
                    width={52}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  {groups.map((group, i) => {
                    const color = getGroupColor(group, groupBy, i);
                    return (
                      <Area
                        key={group}
                        type="monotone"
                        dataKey={group}
                        stackId="1"
                        stroke={color}
                        fill={color}
                        fillOpacity={0.35}
                        hide={!visibleGroups.includes(group)}
                        strokeWidth={2}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--v11-surface))' }}
                      />
                    );
                  })}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Legend                                                             */}
      {/* ----------------------------------------------------------------- */}
      {hasChartData && groups.length > 0 && (
        <div className="px-4 pb-3 flex items-center justify-center gap-3 flex-wrap">
          {groups.map((group, i) => {
            const isVisible = visibleGroups.includes(group);
            const color = getGroupColor(group, groupBy, i);
            const platformCfg = groupBy === 'platform' ? getPlatformIcon(group) : null;

            return (
              <button
                key={group}
                onClick={() => toggleGroup(group)}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all',
                  isVisible
                    ? 'text-v11-text-primary hover:bg-v11-surface-hover'
                    : 'text-v11-text-muted opacity-40 line-through hover:opacity-60'
                )}
              >
                {platformCfg ? (
                  <span style={{ color: isVisible ? color : undefined }}>{platformCfg.icon}</span>
                ) : (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: isVisible ? color : '#64748b' }}
                  />
                )}
                {platformCfg?.name || group}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
